<script setup>
import { Head, Link, useForm } from '@inertiajs/vue3';
import DarkModeToggle from "@/Components/DarkModeToggle.vue"; // تأكد من وجوده لديك
import AuthenticationCard from '@/Components/AuthenticationCard.vue';
import AuthenticationCardLogo from '@/Components/AuthenticationCardLogo.vue';
import Checkbox from '@/Components/Checkbox.vue';
import InputError from '@/Components/InputError.vue';
import InputLabel from '@/Components/InputLabel.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import TextInput from '@/Components/TextInput.vue';
import { useI18n } from "vue-i18n";

const { t } = useI18n();

defineProps({
    canResetPassword: Boolean,
    status: String,
});

const form = useForm({
    email: '',
    password: '',
    remember: false,
});

const submit = () => {
    form.transform(data => ({
        ...data,
        remember: form.remember ? 'on' : '',
    })).post(route('login'), {
        onSuccess: () => {
            form.reset('password');
            Toast.fire({
                icon: 'success',
                title: t('success_login') // يفضل وضع مفتاح ترجمة ثابت هنا للنجاح
            });
            window.location.reload();
        },
        onError: () => {
            Toast.fire({
                icon: 'error',
                title: t('login_failed') // يفضل وضع مفتاح ترجمة ثابت هنا للفشل
            });
        },
        onFinish: () => {
            form.reset('password');
            // تم إزالة reload هنا لكي لا تموت أخطاء الـ Validation وتختفي فوراً
        },
    });
};
</script>

<template>

    <Head :title="$t('login')" />

    <div :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'"
        class="min-h-screen flex bg-slate-50 dark:bg-gray-950 font-sans transition-colors duration-300 relative">

        <div class="absolute top-6 inset-inline-start-6 z-30" :class="$i18n.locale === 'ar' ? 'left-6' : 'right-6'">
            <DarkModeToggle />
        </div>

        <div
            class="flex-1 flex flex-col justify-center py-12 px-6 sm:px-12 lg:flex-none lg:w-[480px] xl:w-[520px] bg-white dark:bg-gray-900 shadow-2xl relative z-10 transition-colors duration-300">
            <div class="mx-auto w-full max-w-sm lg:w-96">

                <div class="text-start mb-8 group">
                    <div
                        class="inline-flex items-center justify-center p-3 bg-gradient-to-br from-indigo-50 to-slate-100 dark:from-indigo-950/30 dark:to-slate-900/50 rounded-2xl mb-5 border border-indigo-100/50 dark:border-indigo-900/30 shadow-sm transition-all duration-300 group-hover:scale-105 group-hover:shadow-md group-hover:ring-4 group-hover:ring-indigo-500/10">
                        <img src="https://res.easyhome.sa/assets/images/site/logo/logo.png" alt="Logo"
                            class="max-w-[50px] sm:max-w-[65px] h-auto object-contain" />
                    </div>
                    <h2 class="text-2xl font-black text-slate-900 dark:text-white tracking-tight transition-colors">
                        {{ $t('welcome_back') }}
                    </h2>
                    <p class="mt-2 text-sm text-slate-500 dark:text-gray-400 font-medium leading-relaxed">
                        {{ $t('login_subtitle') }}
                    </p>
                </div>

                <div v-if="status"
                    class="mb-6 p-4 bg-emerald-50/60 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900/30 rounded-2xl font-bold text-xs text-emerald-600 dark:text-emerald-400 text-start shadow-sm backdrop-blur-sm animate-fadeIn">
                    {{ status }}
                </div>

                <form @submit.prevent="submit" class="space-y-5 text-start">

                    <div class="space-y-2">
                        <InputLabel for="email" :value="$t('email')"
                            class="text-xs font-bold text-slate-700 dark:text-gray-300 tracking-wide" />
                        <div class="relative group">
                            <div
                                class="absolute inset-y-0 inset-inline-start-0 flex items-center ps-3.5 pointer-events-none text-slate-400 dark:text-gray-500 group-focus-within:text-indigo-600 dark:group-focus-within:text-indigo-400 transition-colors duration-200">
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                                </svg>
                            </div>
                            <TextInput id="email" v-model="form.email" type="email"
                                class="mt-1 block w-full !rounded-xl border-gray-200 dark:border-gray-700 dark:bg-gray-800/60 dark:text-white text-sm focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-600 transition-all duration-200 ps-10 text-start placeholder-slate-400 dark:placeholder-gray-500"
                                required autofocus autocomplete="username" :placeholder="$t('enter_email')" />
                        </div>
                        <InputError v-if="form.errors.email"
                            class="mt-1.5 text-[11px] font-bold text-red-500 animate-slideDown"
                            :message="$t(form.errors.email)" />
                    </div>

                    <div class="space-y-2">
                        <div class="flex items-center justify-between">
                            <InputLabel for="password" :value="$t('password')"
                                class="text-xs font-bold text-slate-700 dark:text-gray-300 tracking-wide" />
                            <Link v-if="canResetPassword" :href="route('password.request')"
                                class="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors duration-150">
                                {{ $t('forgot_password') }}
                            </Link>
                        </div>
                        <div class="relative group">
                            <div
                                class="absolute inset-y-0 inset-inline-start-0 flex items-center ps-3.5 pointer-events-none text-slate-400 dark:text-gray-500 group-focus-within:text-indigo-600 dark:group-focus-within:text-indigo-400 transition-colors duration-200">
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z">
                                    </path>
                                </svg>
                            </div>
                            <TextInput id="password" v-model="form.password" type="password"
                                class="mt-1 block w-full !rounded-xl border-gray-200 dark:border-gray-700 dark:bg-gray-800/60 dark:text-white text-sm focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-600 transition-all duration-200 ps-10 text-start placeholder-slate-400 dark:placeholder-gray-500"
                                required autocomplete="current-password" :placeholder="$t('enter_password')" />
                        </div>
                        <InputError v-if="form.errors.password"
                            class="mt-1.5 text-[11px] font-bold text-red-500 animate-slideDown"
                            :message="$t(form.errors.password)" />
                    </div>

                    <!-- <div class="flex items-center justify-between pt-1">
                        <label class="flex items-center cursor-pointer select-none group">
                            <Checkbox v-model:checked="form.remember" name="remember"
                                class="rounded-md border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-800 dark:border-gray-700 transition-all duration-150" />
                            <span
                                class="text-xs font-semibold text-slate-600 dark:text-gray-400 group-hover:text-slate-900 dark:group-hover:text-gray-200 transition-colors duration-150"
                                :class="$i18n.locale === 'ar' ? 'mr-2.5' : 'ml-2.5'">
                                {{ $t('remember_me') }}
                            </span>
                        </label>
                    </div> -->

                    <div class="pt-2">
                        <PrimaryButton
                            class="w-full !py-3 !rounded-xl bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 dark:from-indigo-600 dark:to-indigo-600 dark:hover:from-indigo-500 dark:hover:to-indigo-500 text-white font-bold text-sm tracking-wide shadow-lg shadow-indigo-600/20 dark:shadow-none flex items-center justify-center transition-all duration-200 active:scale-[0.98] disabled:opacity-50 border-0"
                            :class="{ 'opacity-50 pointer-events-none': form.processing }" :disabled="form.processing">
                            <svg v-if="form.processing" class="animate-spin h-4 w-4 text-white"
                                :class="$i18n.locale === 'ar' ? '-mr-1 ml-3' : '-ml-1 mr-3'" fill="none"
                                viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                    stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                                </path>
                            </svg>
                            {{ form.processing ? $t('logging_in') : $t('login') }}
                        </PrimaryButton>
                    </div>
                </form>

                <div class="mt-12 text-center">
                    <p class="text-xs text-slate-400 dark:text-gray-500 font-medium">
                        &copy; {{ new Date().getFullYear() }} {{ $t('system_footer') }}
                    </p>
                </div>

            </div>
        </div>

        <div
            class="hidden lg:block relative flex-1 w-0 bg-slate-100 dark:bg-gray-950 overflow-hidden transition-colors duration-300">

            <div
                class="absolute inset-0 bg-gradient-to-br from-slate-100 via-indigo-50/40 to-slate-200 dark:from-gray-950 dark:via-slate-900/60 dark:to-slate-950 transition-colors duration-300">
            </div>

            <div class="absolute inset-0 opacity-[0.35] dark:opacity-[0.15] mix-blend-overlay dark:mix-blend-normal"
                style="background-image: linear-gradient(to right, rgba(99, 102, 241, 0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(99, 102, 241, 0.1) 1px, transparent 1px); background-size: 32px 32px;">
            </div>

            <div class="absolute -top-20 -right-20 w-96 h-96 bg-indigo-400/20 dark:bg-indigo-600/15 rounded-full blur-[80px] animate-pulse"
                style="animation-duration: 8s;"></div>
            <div class="absolute top-1/3 -left-20 w-[450px] h-[450px] bg-purple-400/15 dark:bg-purple-600/10 rounded-full blur-[100px] animate-bounce"
                style="animation-duration: 12s;"></div>
            <div class="absolute -bottom-10 right-1/4 w-80 h-80 bg-sky-400/15 dark:bg-sky-500/10 rounded-full blur-[70px] animate-pulse"
                style="animation-duration: 6s;"></div>

            <div class="absolute inset-0 opacity-[0.4] dark:opacity-[0.1]"
                style="background-image: radial-gradient(rgba(79, 70, 229, 0.1) 1.5px, transparent 0); background-size: 16px 16px;">
            </div>

            <div class="absolute inset-0 flex flex-col justify-between p-16 z-20 text-start select-none">

                <div class="flex items-center gap-2.5">
                    <span
                        class="h-3.5 w-3.5 rounded-full bg-indigo-600 dark:bg-indigo-500 shadow-md shadow-indigo-600/50 animate-pulse"></span>
                    <span
                        class="text-xs font-extrabold uppercase tracking-widest text-indigo-700 dark:text-indigo-400 bg-indigo-100/60 dark:bg-indigo-950/40 px-3 py-1 rounded-full backdrop-blur-sm transition-colors">
                        {{ $t('system_badge') }}
                    </span>
                </div>

                <div
                    class="max-w-xl p-8 rounded-3xl bg-white/30 dark:bg-gray-900/20 border border-white/40 dark:border-white/5 shadow-2xl shadow-slate-200/50 dark:shadow-none backdrop-blur-md transition-all duration-300">
                    <h1 class="text-4xl font-black leading-tight tracking-tight text-slate-800 dark:text-white mb-4">
                        {{ $t('hero_title') }}
                    </h1>
                    <p class="text-base text-slate-600 dark:text-gray-300 font-medium leading-relaxed">
                        {{ $t('hero_subtitle') }}
                    </p>
                </div>

                <div
                    class="flex items-center justify-between text-xs text-slate-500 dark:text-gray-400 border-t border-slate-200/60 dark:border-slate-800/60 pt-6 backdrop-blur-[2px]">
                    <div class="flex items-center gap-1.5 font-medium">
                        <svg class="h-4 w-4 text-emerald-600 dark:text-emerald-500" fill="none" stroke="currentColor"
                            viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5"
                                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z">
                            </path>
                        </svg>
                        <span>{{ $t('secure_badge') }}</span>
                    </div>
                    <!-- <span
                        class="font-black tracking-wider text-indigo-600 dark:text-indigo-400 hover:opacity-80 transition-opacity">ToutaWorld</span> -->
                </div>
            </div>
        </div>

    </div>
</template>